import { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../services/apiClient';

/**
 * Loads financial work items (margin alerts + staffing gaps) for finance PMs.
 *
 * @param {{ enabled?: boolean, startDate?: string, endDate?: string }} [options]
 * @returns {object} Financial work item state.
 */
export const useFinancialWorkItems = (options = {}) => {
    const { enabled = true, startDate, endDate } = options;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [refreshToken, setRefreshToken] = useState(0);

    const periodKey = useMemo(
        () => `${startDate || ''}|${endDate || ''}`,
        [startDate, endDate]
    );

    const refresh = useCallback(() => {
        setRefreshToken((value) => value + 1);
    }, []);

    useEffect(() => {
        if (!enabled) {
            setData(null);
            setError(null);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const params = {};
                if (startDate) {
                    params.start_date = startDate;
                }
                if (endDate) {
                    params.end_date = endDate;
                }
                const response = await apiClient.get('/projects/financial-work-items/', { params });
                if (!cancelled) {
                    setData(response.data);
                }
            } catch (fetchError) {
                if (!cancelled) {
                    setError(
                        fetchError.response?.data?.error
                        || fetchError.response?.data?.detail
                        || fetchError.message
                        || 'Failed to load financial work items'
                    );
                    setData(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [enabled, periodKey, refreshToken]);

    return {
        loading,
        error,
        workItems: data?.work_items ?? [],
        totalWorkItems: data?.total_work_items ?? 0,
        marginAlertCount: data?.margin_alert_count ?? 0,
        staffingGapCount: data?.staffing_gap_count ?? 0,
        period: data?.period ?? null,
        refresh,
    };
};
